import Bugsnag from '@bugsnag/js'
import { startOfMonth, endOfMonth } from 'date-fns'

import axios from 'utils/axios'

import type { ExternalReviewSettings } from './ExternalReviewSettings'
import type { UpcomingPosts } from './UpcomingPosts'

const fetchPosts = async (reviewKey: string) => {
  try {
    const { data } = await axios.get<UpcomingPosts[]>(
      `/v2/collaboration/external-review/${reviewKey}/posts`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

const fetchPostPreview = async (reviewKey: string, postId: string) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/posts/${postId}/preview`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

const fetchPostsPreview = async (reviewKey: string) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/posts/preview`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

const fetchPostSkedPreview = async (reviewKey: string, postId: string) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/posts/${postId}/preview`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

const fetchSetting = async (
  reviewKey: string
): Promise<ExternalReviewSettings> => {
  try {
    const { data } = await axios.get<ExternalReviewSettings | undefined>(
      `/v2/collaboration/external-review/${reviewKey}/settings`
    )
    if (data === undefined) {
      // this appears to be what was returned before,
      // so let's handle it for now
      throw new Error('Error fetching settings. Result was undefined')
    }
    return data
  } catch (error: any) {
    console.debug(error)

    if (error instanceof Error) {
      Bugsnag.notify(error, function (event) {
        event.addMetadata('url', {
          url: `/v2/collaboration/external-review/${reviewKey}/settings`,
          payload: {
            reviewKey,
          },
        })
      })
      throw error
    } else {
      // Convert non-Error objects to Error before throwing
      const errorObj = new Error(
        typeof error === 'string' ? error : JSON.stringify(error)
      )
      Bugsnag.notify(errorObj, function (event) {
        event.addMetadata('url', {
          url: `/v2/collaboration/external-review/${reviewKey}/settings`,
          payload: {
            reviewKey,
          },
        })
        event.addMetadata('originalError', {
          value: error,
        })
      })
      throw errorObj
    }
  }
}

const postAction = async (
  reviewKey: string,
  postId: string,
  statusKey: string
) => {
  try {
    const { data } = await axios.put(
      `/v2/collaboration/external-review/${reviewKey}/${postId}/status/${statusKey}`
    )

    return data
  } catch (error: any) {
    console.error(error)
    if (error instanceof Error) {
      Bugsnag.notify(error, function (event) {
        event.addMetadata('url', {
          url: `/v2/collaboration/external-review/${reviewKey}/${postId}/status/${statusKey}`,
          payload: {
            post_status_key: statusKey,
          },
        })
      })
    }
  }
}

const postComment = async (
  reviewKey: string,
  postId: string,
  comment: string,
  commentId: string
) => {
  try {
    const { data } = await axios.patch(
      `/v2/collaboration/external-review/${reviewKey}/${postId}/comments/${commentId}`,
      {
        text: comment,
      }
    )

    return data
  } catch (error: any) {
    console.error(error)
    if (error instanceof Error) {
      Bugsnag.notify(error, function (event) {
        event.addMetadata('url', {
          url: `/v2/collaboration/external-review/${reviewKey}/${postId}/comments/${commentId}`,
          payload: {
            text: comment,
          },
        })
      })
    }
  }
}

const fetchComment = async (reviewKey: string, postId: string) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/${postId}/comments`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

// for ig only, desktop only
const fetchGridPreview = async (reviewKey: string, postId: string) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/posts/${postId}/grid-preview`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

// for mobile grid preview
const fetchGridPreviewByAccount = async (
  reviewKey: string,
  accountId: string
) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/accounts/${accountId}/grid-preview`
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

// desktop only
const fetchCalendar = async (reviewKey: string, date: Date) => {
  try {
    const { data } = await axios.get(
      `/v2/collaboration/external-review/${reviewKey}/posts`,
      {
        params: {
          excludeDrafts: true,
          dateFrom: startOfMonth(date),
          dateTo: endOfMonth(date),
        },
      }
    )

    return data
  } catch (error) {
    console.error(error)
  }
}

export {
  fetchSetting,
  fetchPosts,
  fetchPostPreview,
  fetchPostsPreview,
  fetchGridPreview,
  fetchGridPreviewByAccount,
  fetchPostSkedPreview,
  postAction,
  postComment,
  fetchComment,
  fetchCalendar,
}
